import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { testimonialsData, Testimonial } from '@/mocks/testimonials';

interface TestimonialsProps {
  data?: Testimonial[];
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const Testimonials = ({ data }: TestimonialsProps) => {
  const testimonials = data || testimonialsData;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <svg
        key={index}
        className={`w-5 h-5 ${
          index < rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
    >
      {testimonials.map((testimonial) => (
        <motion.div
          key={testimonial.id}
          variants={itemVariants}
          whileHover={{ y: -8 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
        >
          {/* Rating Stars */}
          <div className="flex gap-1 mb-4">{renderStars(testimonial.rating)}</div>

          {/* Comment */}
          <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed mb-6 flex-1">
            "{testimonial.comment}"
          </p>

          {/* User Info */}
          <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
            <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0">
              <Image
                src={testimonial.avatar}
                alt={testimonial.name}
                fill
                className="object-cover"
                sizes="56px"
              />
            </div>
            <div className="flex flex-col">
              <h4 className="text-[16px] md:text-[18px] font-semibold text-gray2">
                {testimonial.name}
              </h4>
              <p className="text-[14px] text-gray-500">{testimonial.role}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default Testimonials;

